import Cookie from 'vue-cookie'
import axios from 'axios'

// Initial state
const state = {
  userName: '',
  fullName: '',
  organizationName: '',
  temporaryPassword: false,
  token: '',
  roles: {},
  superAdmin: false,
  adminToken: '',
  impersonating: false
}

// Mutations
const mutations = {
  loggedIn (state, userContext) {
    console.log('Logged in with user name \'' + userContext.userName + '\'')
    state.userName = userContext.userName
    state.fullName = userContext.fullName
    state.organizationName = userContext.organizationName
    state.temporaryPassword = userContext.temporaryPassword
    state.token = userContext.token
    state.roles = userContext.roles
    state.superAdmin = userContext.superAdmin
    state.impersonating = false
    document.title = userContext.organizationName ? userContext.organizationName : 'Calasdo'
  },
  loggedOut (state) {
    console.log('Logged out')
    state.userName = ''
    state.fullName = ''
    state.organizationName = ''
    state.temporaryPassword = false
    state.roles = ''
    state.token = ''
    state.superAdmin = false
    state.impersonating = false
  },
  startImpersonate (state, userContext) {
    state.adminToken = state.token
    state.impersonating = true
    state.userName = userContext.userName
    state.fullName = userContext.fullName
    state.organizationName = userContext.organizationName
    state.temporaryPassword = false
    state.token = userContext.token
    state.roles = userContext.roles
    state.superAdmin = false
    document.title = userContext.organizationName ? userContext.organizationName : 'Calasdo'
  },
  endImpersonate (state, userContext) {
    state.adminToken = ''
    state.impersonating = false
    state.userName = userContext.userName
    state.fullName = userContext.fullName
    state.organizationName = userContext.organizationName
    state.temporaryPassword = false
    state.roles = userContext.roles
    state.token = userContext.token
    state.superAdmin = userContext.superAdmin
    document.title = userContext.organizationName ? userContext.organizationName : 'Calasdo'
  }
}

// Actions
const actions = {
  initialize ({ commit, state }) {
    console.log('Initialize session')
    let userContext = Cookie.get('USER_CONTEXT')
    if (userContext) {
      commit('loggedIn', JSON.parse(userContext))
    }
  },
  login ({ commit, state }, ctx) {
    return new Promise((resolve, reject) => {
      console.log('Login with user name ' + ctx.userName)

      // Call the backend
      axios.post('/backend/ui/session/login', ctx)
        .then(response => {
          let userContext = response.data
          if (userContext) {
            Cookie.set('USER_CONTEXT', JSON.stringify(userContext))

            commit('loggedIn', userContext)

            axios.defaults.headers.common = {'Authorization': 'Bearer ' + userContext.token}

            localStorage.setItem('SessionToken', userContext.token)

            resolve('Successfully logged in')
          } else {
            console.log('Unknown error during login')
            let errorMessage = 'Unknown error occurred during login'
            reject(Error(errorMessage))
          }
        })
        .catch(error => {
          reject(Error(error.response.data.message))
        })
    })
  },
  tokenLogin ({ commit, state }, ctx) {
    return new Promise((resolve, reject) => {
      // console.log('Re-using session from token ' + ctx.token)

      // Call the backend
      axios.post('/backend/ui/session/tokenLogin', ctx)
        .then(response => {
          let userContext = response.data

          Cookie.set('USER_CONTEXT', JSON.stringify(userContext))

          commit('loggedIn', userContext)

          axios.defaults.headers.common = {'Authorization': 'Bearer ' + userContext.token}

          localStorage.setItem('SessionToken', userContext.token)

          resolve('Successfully logged in with token')
        })
        .catch(error => {
          console.log('Login error: ' + JSON.stringify(error))
          let errorMessage = ''
          if (error.response.data.message && error.response.data.message !== '') {
            errorMessage = error.response.data.message
          } else {
            errorMessage = 'Unknown error occurred during login'
          }
          reject(Error(errorMessage))
        })
    })
  },
  logout ({ commit, state }) {
    return new Promise((resolve, reject) => {
      Cookie.delete('USER_CONTEXT')

      // Call the backend
      axios.post('/backend/ui/session/logout')
        .then(response => {
          localStorage.removeItem('SessionToken')
          axios.defaults.headers.common = {}
          resolve('Successfully logged out')
        })
        .catch(error => {
          console.log('Logout error: ' + JSON.stringify(error))
          let errorMessage = ''
          if (error.response.data.message && error.response.data.message !== '') {
            errorMessage = error.response.data.message
          } else {
            errorMessage = 'Unknown error occurred during logout'
          }
          axios.defaults.headers.common = {}
          reject(Error(errorMessage))
        })

      commit('loggedOut')
    })
  },
  clear ({ commit, state }) {
    return new Promise((resolve, reject) => {
      Cookie.delete('USER_CONTEXT')
      localStorage.removeItem('SessionToken')
      axios.defaults.headers.common = {}
      commit('loggedOut')
    })
  },
  impersonate ({ commit, state }, user) {
    return new Promise((resolve, reject) => {
      axios.post('/backend/ui/session/impersonate/user/' + user.id)
        .then(response => {
          let userContext = response.data

          Cookie.set('USER_CONTEXT', JSON.stringify(userContext))

          commit('startImpersonate', userContext)

          axios.defaults.headers.common = {'Authorization': 'Bearer ' + userContext.token}

          resolve('Success')
        })
        .catch(error => {
          reject(Error(error.response.data.message))
        })
    })
  },
  endImpersonation ({ commit, state }, ctx) {
    return new Promise((resolve, reject) => {
      axios.post('/backend/ui/session/logout')
        .then(response => {
          axios.post('/backend/ui/session/tokenLogin', { token: state.adminToken })
            .then(response => {
              let userContext = response.data

              Cookie.set('USER_CONTEXT', JSON.stringify(userContext))

              commit('endImpersonate', userContext)

              axios.defaults.headers.common = {'Authorization': 'Bearer ' + userContext.token}

              resolve('Stopped impersonation')
            })
            .catch(error => {
              let errorMessage = ''
              if (error.response.data.message && error.response.data.message !== '') {
                errorMessage = error.response.data.message
              } else {
                errorMessage = 'Unknown error occurred'
              }
              reject(Error(errorMessage))
            })
        })
        .catch(error => {
          console.log('Logout error: ' + JSON.stringify(error))
          let errorMessage = ''
          if (error.response.data.message && error.response.data.message !== '') {
            errorMessage = error.response.data.message
          } else {
            errorMessage = 'Unknown error occurred during logout'
          }
          reject(Error(errorMessage))
        })
    })
  }
}

// Getters
const getters = {
  hasRole: (state) => (roles) => {
    let res = false
    if (state.roles && Array.isArray(state.roles)) {
      if (state.superAdmin) {
        res = true
      } else {
        for (let role of roles) {
          if (state.roles.includes(role) || (role !== 'SUPER_ADMIN' && state.roles.includes('ADMIN'))) {
            res = true
            break
          }
        }
      }
    }
    // console.log('Has ' + roles + ': ' + res)
    return res
  },
  hasNotRole: (state) => (roles) => {
    let res = true
    if (state.superAdmin && roles && roles.includes('SUPER_ADMIN')) {
      res = false
    } else if (state.roles && state.roles.length > 0 && Array.isArray(state.roles)) {
      for (let role of roles) {
        if (state.roles.includes(role)) {
          res = false
          break
        }
      }
    }
    // console.log('Has not ' + roles + ': ' + res)
    return res
  },
  hasAdministratorRole: (state) => {
    return state.roles && state.roles.includes('ADMIN')
  },
  isSuperAdmin: (state) => {
    return state.superAdmin
  },
  isConnected: (state) => {
    // console.log('Is connected? ' + state.connected)
    return state.connected
  },
  isImpersonating: (state) => {
    return state.impersonating
  },
  isLoggedIn: (state) => {
    return state.token !== ''
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/session.js